import React, { PropsWithChildren, ReactNode, useEffect, useState } from "react";
import { useTheme, Theme } from '@mui/material/styles';
import { makeStyles, createStyles } from '@mui/styles';
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import PagedResult from "../../submodules/fox-typescript/core/PagedResult";
import { Skeleton, TableHead, TableSortLabel } from "@mui/material";
import { CSSProperties } from "@mui/styles/withStyles";
import useInterval from "../../hooks/useInterval";
import GenericTableRow from "./GenericTableRow";

const paginatorStyle = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexShrink: 0,
      marginLeft: theme.spacing(2.5),
    },
  })
);

interface TablePaginationActionsProps {
  /**
   * Total number of rows
   */
  count: number;
  /**
   * Actual page
   */
  page: number;
  /**
   * Number of rows per page
   */
  rowsPerPage: number;
  /**
   * Callback fired when a new page is requested.
   */
  onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const classes = paginatorStyle();
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton onClick={handleLastPageButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
}

const tableStyles = makeStyles({
  table: {
    minWidth: 500,
  },
  tableHeader: {
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
  },
  pointer: {
    cursor: "pointer",
  },
  none: {},
  paginationContainer: {
    "& .MuiTablePagination-spacer": {
      flex: "none",
    },
  },
  stickyPaginationContainer: {
    position: "sticky",
    bottom: 0,
    zIndex: 1000,
    backgroundColor: "white",
    "& .MuiTablePagination-spacer": {
      flex: "none",
    },
  },
});

export interface ColumnSpec<T> {
  header: string;
  content: (instance: T) => JSX.Element;
  icon?: JSX.Element;
  orderBy?: string;
}

interface Props<T> {
  /**
   * Function that will be triggered every time that data has to be fetched
   * e.g. when the UI loads, when a page changes, etc.
   */
  fetchData: (page: number, rowsPerPage: number, sortBy?: string, sortDir?: string) => Promise<PagedResult<T>>;
  /**
   * This prop should be updated every time the list of T changed, thus triggering a potential update.
   */
  lastUpdateTimestamp: number;
  /**
   * Specs for each of the columns of the table, including header names, cell builders, etc. Null values are provided as a declarative way of
   * including/excluding columns in a functional way - for instance, doing something like (`user.isEmployee() ? {header: "Admission date", content: ...} : null`)
   */
  columns: (ColumnSpec<T> | null)[];
  /**
   * whether pagination section of the table will be rendered or not
   */
  enablePagination?: boolean;
  /**
   * Action to be executed when clicking on an element in the table
   */
  onElementClick?: (instance: T) => void;
  /**
   * Redefines the container for the table, overriding the existing one (a Paper)
   */
  tableContainer?: (props: { children: React.ReactNode }) => JSX.Element;
  /**
   * Style for table headers
   */
  stylesForHeader?: CSSProperties;
  /**
   * Style for "actions" header (specific header)
   */
  stylesForActionsHeader?: CSSProperties;
  /**
   * Apply styles to the row according to some attribute of the instance
   */
  stylesForRowIfCondition?: (instance: T) => React.CSSProperties | undefined;
  /**
   * Render only the rows that satisfy the given condition
   */
  renderRowIfCondition?: (instance: T) => boolean | undefined;
  /**
   * Number of page to show
   */
  page?: number;
  /**
   * Number of rows to show in a page
   */
  rowsPerPage?: number;
  /**
   * Time in millis indicating how often the data should be refreshed by querying the backend
   */
  refreshTime?: number;
  /**
   * If `true`, the rows that has no Actions the menu won't be shown.
   */
  hideEmptyActionMenu?: boolean;
  /**
   * Default order direction of the rows.
   * Default value is DESC
   */
  defaultSortDir?: OrderDirection;
  /**
   * Default property used for sorting
   */
  defaultSortBy?: string;
  /**
   * If `true`, the size of the table will be "small"
   */
  denseTable?: boolean;
  /**
   * Component to render if table data is empty.
   * Default value is "No data to display"
   */
  tableEmptyComponent?: JSX.Element;
  /**
   * Number of skeleton rows to render until the first fetch resolves.
   * If not set, the body stays empty during that first fetch.
   */
  loadingRowCount?: number;

  /**
   * Variable to trigger and apply delay to fetch
   */
  delayFetch?: string;
  /**
   * If true, pass null to
   * refresh function interval
   */
  disableAutoFetch?: Boolean;

  useAsContainer?: boolean;
  /**
   * If true, the table header will stick to the top as the user scrolls down the table
   */
  stickyHeader?: boolean;

  /**
   *  The maximum height of the table container.
   *  If the table exceeds this height, it will become scrollable
   */
  maxHeight?: string;

  /**
   * If true, the table pagination will stick to the bottom as the user scrolls down the table
   */
  stickyPagination?: boolean;

  /**
   * If `true`, the rows will be highlighted on hover
   */
  highlightOnHover?: boolean;
}

export enum OrderDirection {
  DESC = "desc",
  ASC = "asc",
}

/**
 * Component used to render a table of T type items,
 * Params:
 * count: the total number of rows in the table.
 * page: the current page number (zero-based).
 * rowsPerPage: the number of rows per page.
 */
export default function GenericTable<T>(props: PropsWithChildren<Props<T>>) {
  const classes = tableStyles();
  const [page, setPage] = useState(props.page || 0);
  const [rows, setRows] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(props.rowsPerPage ?? 5);
  const [sortDir, setSortDir] = useState<OrderDirection>(props.defaultSortDir || OrderDirection.DESC);
  const [sortBy, setSortBy] = useState<string>(props.defaultSortBy || "");
  const [hasLoaded, setHasLoaded] = useState(false);

  useInterval(
    () => {
      fetch();
    },
    props.disableAutoFetch ? null : props.refreshTime ?? 50000
  );

  useEffect(() => {
    if (!props.delayFetch) {
      fetch();
    } else {
      const delayDebounceFn = setTimeout(() => {
        fetch();
      }, 1000);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [page, rowsPerPage, props.lastUpdateTimestamp, sortBy, sortDir, props.fetchData, props.delayFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetch = () => {
    props.fetchData(page, rowsPerPage, sortBy, sortDir).then((result) => {
      setRows(result.content);
      setTotalCount(result.totalElements);
      // prevents the table from staying on an empty page when data is filtered
      if (page * rowsPerPage >= result.totalElements) {
        setPage(0);
      }
    }).finally(() => setHasLoaded(true));
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderPagination = props.enablePagination === undefined ? true : props.enablePagination;

  const columns = props.columns.filter((column) => !!column);

  const removeDuplicates = (rowsPerPage: number[]) => {
    const uniqueRowsPerPage: Set<number> = new Set(rowsPerPage);
    return Array.from(uniqueRowsPerPage);
  };

  const rowsPerPageOptions: number[] = props.rowsPerPage
    ? removeDuplicates([5, 10, 25, props.rowsPerPage].sort((n1, n2) => n1 - n2))
    : [5, 10, 25];

  const handleSort = (index: number) => {
    const { orderBy } = props.columns[index]!!;
    const isAsc = sortBy === orderBy && sortDir === OrderDirection.ASC;
    !!orderBy && setSortBy(orderBy);
    !!orderBy && setSortDir(isAsc ? OrderDirection.DESC : OrderDirection.ASC);
  };

  const renderHeaderLabel = (header: string, icon?: JSX.Element) => {
    let customStyles =
      header === "Actions" ? { ...props?.stylesForHeader, ...props.stylesForActionsHeader } : { ...props?.stylesForHeader };

    return (
      // @ts-ignore
      <span className={classes.tableHeader} style={customStyles}>
        {icon}
        {header}
      </span>
    );
  };

  const renderHeader = ({ orderBy, header, icon }: ColumnSpec<T>, index: number) => {
    const hasSort = typeof orderBy !== "undefined";

    if (!hasSort) {
      return renderHeaderLabel(header, icon);
    }

    return (
      <TableSortLabel
        active={sortBy === orderBy}
        direction={sortBy === orderBy ? sortDir : OrderDirection.DESC}
        onClick={() => handleSort(index)}
      >
        {renderHeaderLabel(header, icon)}
      </TableSortLabel>
    );
  };

  const emptyComponent = props.tableEmptyComponent || <div style={{ textAlign: "center" }}>No data to display</div>;

  const getRowChildren = (): JSX.Element => {
    const childrenComponent = props.children as JSX.Element;

    return {...childrenComponent, props: {...childrenComponent.props, columns}};
  }

  const LOADING_ROWS = <>
    {Array.from({ length: props.loadingRowCount ?? 0 }).map((_, rowIndex) => (
      <TableRow key={`loading-row-${rowIndex}`} data-testid="generic-table-loading-row">
        {columns.map((_, columnIndex) => (
          <TableCell key={columnIndex}>
            <Skeleton variant="text" width={columnIndex === 0 ? "60%" : "80%"} />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>

  const TABLE_ROWS = <>
    {!rows?.length && (
      <TableRow>
        <TableCell colSpan={columns?.length}>{emptyComponent}</TableCell>
      </TableRow>
    )}
    {props.useAsContainer && getRowChildren()}
    {!props.useAsContainer && rows?.map((row, index) => {
      if (!!props.renderRowIfCondition && !props.renderRowIfCondition(row)) return;
      return (
        <GenericTableRow<T>
          key={index}
          columns={columns}
          index={index}
          row={row}
          highlightOnHover={props.highlightOnHover}
          onElementClick={props.onElementClick}
          stylesForRowIfCondition={props.stylesForRowIfCondition}
          hideEmptyActionMenu={props.hideEmptyActionMenu}
        />
      );
    })}
  </>

  return (
    <TableContainer style={{ maxHeight: props.stickyHeader ? props.maxHeight || "65vh" : "" }} component={props.tableContainer || Paper}>
      <Table
        stickyHeader={props.stickyHeader}
        className={classes.table}
        size={props.denseTable ? "small" : undefined}
        aria-label="custom pagination table"
      >
        <TableHead>
          <TableRow>
            {columns?.map((column, index) => (
              <TableCell key={index}>{renderHeader(column!, index)}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {hasLoaded ? TABLE_ROWS : LOADING_ROWS}
        </TableBody>

        {renderPagination && (
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={rowsPerPageOptions}
                colSpan={props.columns?.length || 3}
                count={totalCount}
                rowsPerPage={rowsPerPage}
                className={props.stickyPagination
                  ? classes.stickyPaginationContainer
                  : classes.paginationContainer}
                page={page}
                SelectProps={{
                  inputProps: { "aria-label": "rows per page" },
                  native: true,
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
}

export function SimpleTable<T>(props: {
  rows: T[];
  columns: (ColumnSpec<T> | null)[];
  stylesForHeader?: CSSProperties;
  tableContainer?: (props: { children: React.ReactNode }) => JSX.Element;
}) {
  return (
    <GenericTable<T>
      fetchData={() => Promise.resolve(PagedResult.fromArray(props.rows))}
      lastUpdateTimestamp={new Date().getTime()}
      enablePagination={false}
      columns={props.columns}
      stylesForHeader={props.stylesForHeader}
      tableContainer={props.tableContainer}
    />
  );
}
